import { Session, SessionStatus } from '@/types';

/**
 * Sign-in state, held by the identity service.
 *
 * This app never holds a token. The identity service runs the OIDC flow, keeps the
 * access and refresh tokens server-side, and the browser carries one opaque
 * HttpOnly cookie scoped to `.campusone.cskytelecoms.com`. That cookie is why a
 * user who signed in at accounts.campusone… arrives here already authenticated.
 *
 * The contract is campusone-identity-service/docs/SSO.md.
 */

/** The identity service, which hosts /auth/* and the /bff proxy. */
const ACCOUNTS_API =
  import.meta.env.VITE_ACCOUNTS_API_URL || 'http://localhost:4000/api/v1';

let sessionListeners: Array<(status: SessionStatus) => void> = [];
let currentStatus: SessionStatus = { status: 'loading', session: null };
let csrfToken: string | null = null;

function notify() {
  sessionListeners.forEach((fn) => fn(currentStatus));
}

/** Echoed back in a header on state-changing requests. */
export function getCsrfToken(): string | null {
  return csrfToken;
}

export async function checkSession(): Promise<SessionStatus> {
  try {
    const res = await fetch(`${ACCOUNTS_API}/auth/session`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });

    // This endpoint answers 200 whether or not anyone is signed in — it reports a
    // fact rather than refusing a request, so an anonymous page load is not an
    // error in the console or in monitoring. `authenticated` is therefore the only
    // thing that decides. Treating a 200 as "signed in", as this did before, lets
    // every anonymous visitor through.
    if (!res.ok) throw new Error(`session check failed: ${res.status}`);

    const body = (await res.json()) as {
      authenticated: boolean;
      user?: {
        subject: string;
        email?: string;
        name?: string;
        gender?: 'male' | 'female' | null;
        preferredUsername?: string;
        realmRoles?: string[];
      };
      csrfToken?: string;
      expiresAt?: string;
    };

    csrfToken = body.csrfToken ?? null;

    if (!body.authenticated || !body.user) {
      currentStatus = { status: 'unauthenticated', session: null };
    } else {
      const session: Session = {
        authenticated: true,
        identityId: body.user.subject,
        email: body.user.email,
        name: body.user.name ?? body.user.preferredUsername,
        gender: body.user.gender ?? null,
        expiresAt: body.expiresAt,
        realmRoles: body.user.realmRoles ?? [],
      };
      currentStatus = { status: 'authenticated', session };
    }
  } catch {
    csrfToken = null;
    currentStatus = { status: 'unauthenticated', session: null };
  }

  notify();
  return currentStatus;
}

/** Whether the signed-in user holds a platform-wide role. UI gating only. */
export function hasRealmRole(role: string): boolean {
  return currentStatus.session?.realmRoles?.includes(role) ?? false;
}

/**
 * Both spellings of the CSKY platform administrator role.
 *
 * `CSKY_PLATFORM_ADMIN` is the canonical name; `platform-admin` is the Keycloak
 * realm role that shipped first and is still on live sessions. Core's
 * PlatformAdminOnly guard accepts both, and so must this — checking only the new
 * name would lock out every administrator signed in today, and checking only the
 * old one would lock out everyone after the rename.
 */
export const PLATFORM_ADMIN_ROLES = ['CSKY_PLATFORM_ADMIN', 'platform-admin'] as const;

/**
 * UI gating only, never authorisation.
 *
 * Anyone can edit a JavaScript variable; what stops them reaching platform data
 * is the guard on the endpoint, which reads the same role out of a signed token.
 * This only decides whether we offer the door.
 */
export function isPlatformAdmin(realmRoles: readonly string[] | undefined): boolean {
  if (!Array.isArray(realmRoles)) return false;
  return PLATFORM_ADMIN_ROLES.some((role) => realmRoles.includes(role));
}

/**
 * Institution-level administrator role codes, assigned per-institution through
 * the platform console or onboarding flow. Not platform-wide roles.
 *
 * EDUCORE_ADMIN is here because an EduCore administrator IS that school's
 * institution administrator — the role catalogue classifies it SCHOOL_ADMIN
 * ("Administers EduCore for one institution"), core's institution-context guard
 * accepts it on the same console, and a console link the shell offers but the
 * guard refuses is the exact failure this list is meant to prevent. This list
 * only opens doors in the UI; every endpoint behind it re-checks the role.
 */
export const INSTITUTION_ADMIN_ROLES = [
  'INSTITUTION_ADMINISTRATOR',
  'PORTAL_ADMINISTRATOR',
  'EDUCORE_ADMIN',
] as const;

/**
 * UI gating only, never authorisation.
 *
 * Checks whether the active institution context carries the institution admin
 * role code. Contexts come from /context/available and are scoped to one
 * institution — unlike realm roles they do not apply across all tenants.
 */
export function isInstitutionAdmin(contextRoleCode: string | undefined): boolean {
  if (!contextRoleCode) return false;
  return INSTITUTION_ADMIN_ROLES.some((role) => contextRoleCode === role);
}

/**
 * Group-level roles whose seat owns the group's money.
 *
 * The group finance manager is the money seat by name; the group president is
 * the group's chief executive and governs the whole surface, money included.
 * The group administrator is deliberately NOT here — that seat runs the group
 * itself, and the dashboard endpoint withholds fee figures from it.
 *
 * UI gating only, never authorisation: the endpoint decides what a context may
 * actually read, and this list only decides whether the panel offers the door.
 */
export const GROUP_FINANCE_ROLES = ['GROUP_FINANCE_MANAGER', 'GROUP_PRESIDENT'] as const;

/** Whether the active group context carries the group's money seat. UI gating only. */
export function isGroupFinance(contextRoleCode: string | undefined): boolean {
  if (!contextRoleCode) return false;
  return GROUP_FINANCE_ROLES.some((role) => contextRoleCode === role);
}

export function getSessionStatus(): SessionStatus {
  return currentStatus;
}

export function onSessionChange(fn: (status: SessionStatus) => void): () => void {
  sessionListeners.push(fn);
  return () => {
    sessionListeners = sessionListeners.filter((l) => l !== fn);
  };
}

/**
 * Straight to the identity service's login endpoint, which redirects to Keycloak.
 *
 * A full-page navigation, not fetch: the login page must be a top-level document
 * for the SSO cookie and any federated provider to work. `return_to` is validated
 * server-side against an allowlist, so this app's origin has to appear in
 * BFF_ALLOWED_RETURN_ORIGINS or the user is returned to the default instead.
 */
export function redirectToLogin(returnUrl?: string): void {
  const url = new URL(`${ACCOUNTS_API}/auth/login`);
  url.searchParams.set('return_to', returnUrl || window.location.href);
  url.searchParams.set('product', 'app');
  window.location.assign(url.toString());
}

export async function logout(): Promise<void> {
  currentStatus = { status: 'unauthenticated', session: null };
  csrfToken = null;
  notify();

  try {
    const res = await fetch(`${ACCOUNTS_API}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ return_to: window.location.origin }),
    });

    if (res.ok) {
      const { logoutUrl } = (await res.json()) as { logoutUrl?: string };
      // Following this is what actually ends the session. Clearing local state
      // alone leaves the Keycloak SSO session alive, so the next page load signs
      // the user back in with no prompt — which looks like logout being broken
      // rather than working.
      if (logoutUrl) {
        window.location.assign(logoutUrl);
        return;
      }
    }
  } catch {
    // Local state is already cleared, which is the part the user can see. Fall
    // through to a reload rather than leaving them on a page that still claims a
    // session.
  }

  window.location.assign('/');
}
